import { useState, memo } from "react";
import {
  Grid,
  Divider,
  Typography,
  Tooltip,
  TextField,
  Button,
  Alert,
  Chip,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ShopifyOauth = ({ formData, handleChange, onConnect, loading }) => {
  console.log(formData);

  const [localError, setLocalError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copiedTooltip, setCopiedTooltip] = useState("");

  const isConnected =
    formData.configuration.authentication.access_token &&
    formData.configuration.authentication.access_token.startsWith("shpat_") &&
    formData.configuration.authentication.authorized;

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleConnectClick = async () => {
    setLocalError("");
    let url = formData.configuration.authentication.shop_url || "";
    const clientId = formData.configuration.authentication.api_key;
    const clientSecret = formData.configuration.authentication.client_secret;

    url = url
      .replace("https://", "")
      .replace("http://", "")
      .replace(/\/$/, "")
      .trim();

    if (!formData.warehouse) {
      setLocalError("Debes seleccionar bodega");
      return;
    }

    if (!url || !url.includes("myshopify.com")) {
      setLocalError("La URL debe ser válida (ej: mitienda.myshopify.com)");
      return;
    }
    if (!clientId) {
      setLocalError("El Client ID es obligatorio");
      return;
    }
    if (!clientSecret) {
      setLocalError("El Client Secret es obligatorio");
      return;
    }

    const { success, redirectUri, error } = await onConnect(
      url,
      {
        clientId,
        clientSecret,
      },
      formData.warehouse,
    );

    if (success) {
      window.location.replace(redirectUri);
    } else {
      setLocalError(error);
    }
  };

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Integración OAuth 2.0 (Nuevo Flujo):</strong> Ingresa las
          credenciales de tu App de Shopify.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Shop URL"
          placeholder="mitienda.myshopify.com"
          value={formData.configuration.authentication.shop_url}
          onChange={(e) => handleChange("shop_url", e.target.value)}
          disabled={isConnected || loading}
          error={localError && localError.includes("URL")}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Client ID (API Key)"
          placeholder="El ID público de tu app"
          value={formData.configuration.authentication.api_key}
          onChange={(e) => handleChange("api_key", e.target.value)}
          disabled={isConnected || loading}
          error={localError && localError.includes("Client ID")}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Client Secret"
          type={showSecret ? "text" : "password"}
          placeholder="Comienza con shpss_..."
          value={formData.configuration.authentication.client_secret}
          onChange={(e) => handleChange("client_secret", e.target.value)}
          disabled={isConnected || loading}
          error={localError && localError.includes("Secret")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowSecret(!showSecret)}
                  edge="end"
                >
                  {showSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {localError && (
        <Grid item xs={12}>
          <Alert severity="error">{localError}</Alert>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "center", mt: 1 }}
      >
        {isConnected ? (
          <Chip
            label="Conectado Exitosamente"
            color="success"
            variant="outlined"
            sx={{ width: "100%", py: 3, fontSize: "1rem" }}
          />
        ) : (
          <Box sx={{ position: "relative", width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleConnectClick}
              disabled={loading}
            >
              {loading ? "Conectando..." : "Guardar y Conectar"}
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  mt: "-12px",
                  ml: "-12px",
                }}
              />
            )}
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Token Webhook"
          value={formData.configuration.authentication.token}
          onChange={(e) => handleChange("token", e.target.value)}
        />
      </Grid>
      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
        Webhooks
      </Typography>
      {formData.configuration?.create_order && formData.configuration?.update_order && formData.configuration?.cancel_order && (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Crear Orden"
            value={formData.configuration.create_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "create" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "create" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.create_order?.url,
                          "create",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Cancelar Orden"
            value={formData.configuration.cancel_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "cancel" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "cancel" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.cancel_order?.url,
                          "cancel",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook Actualizar Orden"
            value={formData?.configuration?.update_order?.url || ""}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copiedTooltip === "update" ? "¡Copiado!" : "Copiar"}
                    arrow
                    open={copiedTooltip === "update" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.update_order?.url,
                          "update",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      )}
    </Grid>
  );
};

export default memo(ShopifyOauth);
